import { Schema, arrayOf } from 'normalizr';
import omit from 'lodash/omit';

import { transformUtil } from '@eb/transformation-utils';
import { snakeToCamel } from '@eb/string-utils';

import { eventsDuck } from '@eb/redux-destination';
import { ONLINE_BANNER_URL } from '../constants/constants';

import {
    ENTITY_TYPE_MAP,
    SEARCH_CRITERIA_TYPE_MAP,
    SEARCH_TYPE_MAP,
    SINGULARIZED_ENTITY_TYPE_MAP,
    EVENT_CARD_TYPE,
} from '../constants';

const {
    utils: { formatTransformedEventsWithCollection },
} = eventsDuck;

export const getTransformedContent = (key, response) => {
    /* Due to how the normalizr library is handling this instantiation
   server side, necessary to do an additional interpolation step. */
    const content = new Schema(`${key}`);

    return transformUtil({
        response,
        schema: {
            [key]: arrayOf(content),
        },
    });
};

export const transformBrowseBucketContent = (contentBuckets) => {
    let entities = {};

    let entityContext = [];

    if (contentBuckets) {
        entityContext = contentBuckets.map((bucket) => {
            const typeKey = ENTITY_TYPE_MAP[bucket.type];

            const { result, entities: parsedEntities } = getTransformedContent(
                typeKey,
                bucket,
            );

            let newEntities = parsedEntities[typeKey];

            // check Type value. If event call the formatter. Can't do it for articles etc
            if (bucket.type === EVENT_CARD_TYPE) {
                newEntities = formatTransformedEventsWithCollection(
                    newEntities,
                    result[typeKey],
                );
            }

            entities = { ...entities, ...newEntities };

            return result;
        });
    }

    return { entities, entityContext };
};

const _addSearchQueryToResults = (results, searchData, type) => ({
    ...results,
    searchQuery: searchData[`${type}_search`],
});

/*
 * Building the pagination in an abstract way, so that
 * we will be able to start handling profiles/articles.
 * But don't include unless the ids for the respective types exist
 *
 * Output:
 *  pagination: {
 *    event: [
 *        ids: [1,2,3,4,5],
 *        object_count: 1234,
 *        page_size: 10,
 *        continuation: asdfasdf
 *   ],
 *   profile: [],
 *   article: [],
 * }
 */
const _constructPagination = (paginationData, pagination, ids, type) => {
    let constructedPagination = paginationData;

    if (ids && pagination) {
        let pageNumberKey = pagination.pageNumber - 1;

        if (!Number.isInteger(pageNumberKey)) {
            // Some API responses do not have a page number, e.g. articles
            pageNumberKey = 0;
        }

        constructedPagination = {
            ...paginationData,
            currentPage: pageNumberKey,
            [type]: {
                [pageNumberKey]: {
                    ...pagination,
                    ids,
                },
            },
        };
    }

    return constructedPagination;
};

const _parseSearchCriterias = (searchData) =>
    SEARCH_CRITERIA_TYPE_MAP.reduce((memo, key) => {
        const schema = new Schema('results');

        const transformedContent = transformUtil({
            response: searchData[key],
            schema,
        });

        return {
            ...memo,
            [snakeToCamel(key)]: omit(
                transformedContent.entities.results[transformedContent.result],
                'pointRadius',
            ),
        };
    }, {});

export const transformSearchBucketContent = (searchData) => {
    let entities = {};

    let paginationData = {};

    let entityContext = [];

    let searchCriterias = {};

    if (searchData) {
        entityContext = SEARCH_TYPE_MAP.map((key) => {
            const type = SINGULARIZED_ENTITY_TYPE_MAP[key];

            let {
                result: { results: ids, pagination, ...results },
                entities: { results: newEntities },
            } = getTransformedContent('results', searchData[key]);

            results = _addSearchQueryToResults(results, searchData, type);
            paginationData = _constructPagination(
                paginationData,
                pagination,
                ids,
                type,
            );

            if (type === EVENT_CARD_TYPE) {
                newEntities = formatTransformedEventsWithCollection(
                    newEntities,
                    ids,
                );
            }

            entities = { ...entities, ...newEntities };

            return { type, ids, ...results };
        });

        searchCriterias = {
            ..._parseSearchCriterias(searchData),
            searchId: searchData['search_id'],
        };
    }

    return {
        entities,
        entityContext,
        searchCriterias,
        pagination: paginationData,
    };
};

export const transformBrandingResponse = (branding, isOnline) => {
    const {
        attribution: {
            name: artistName,
            summary: artistSummary,
            image: { original: { url: artistImageUrl } = {} } = {},
            social_links: artistSocialLinks,
        } = {},
        header: {
            image: {
                original: { url: headerImageUrl } = {},
                url: headerSmallImageUrl,
            } = {},
        } = {},
        middle: {
            image: {
                original: { url: middleImageUrl } = {},
                url: middleSmallImageUrl,
            } = {},
        } = {},
        footer: {
            image: {
                original: { url: footerImageUrl } = {},
                url: footerSmallImageUrl,
            } = {},
        } = {},
        decoration_top_left: {
            image: { original: { url: decorationTopLeftUrl } = {} } = {},
        } = {},
        decoration_top_right: {
            image: { original: { url: decorationTopRightUrl } = {} } = {},
        } = {},
        decoration_bottom_left: {
            image: { original: { url: decorationBottomLeftUrl } = {} } = {},
        } = {},
        decoration_bottom_right: {
            image: { original: { url: decorationBottomRightUrl } = {} } = {},
        } = {},
    } = branding;

    let onlineHeaderImage = null;

    if (isOnline) {
        onlineHeaderImage = ONLINE_BANNER_URL;
    }

    return {
        artistName,
        artistSummary,
        artistImageUrl,
        headerImageUrl: onlineHeaderImage || headerImageUrl,
        headerSmallImageUrl: onlineHeaderImage || headerSmallImageUrl,
        middleImageUrl,
        middleSmallImageUrl,
        footerImageUrl,
        footerSmallImageUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        artistSocialLinks,
    };
};
